import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BackToTopButton } from "@/components/BackToTopButton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy | Grounded"
        description="How Grounded collects, uses, and protects your data when you find and review work-friendly cafes."
        path="/privacy"
      />
      <Header />
      <main className="container max-w-3xl mx-auto px-6 py-10 md:py-16">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-2">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mb-8">
          Effective date: September 8, 2026 ·{" "}
          <Link to="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>
        </p>

        <section className="prose-section space-y-6 text-foreground/90 text-[15px] leading-relaxed">
          <p>
            Grounded ("we", "us", or "our") operates the Grounded mobile application and website. This Privacy Policy explains what data we collect, how we use it, and your choices regarding your information.
          </p>

          <Accordion type="multiple" className="w-full">
            {/* 1 — Data We Collect */}
            <AccordionItem value="data-collect">
              <AccordionTrigger className="text-base font-semibold">
                1. Data We Collect
              </AccordionTrigger>
              <AccordionContent className="space-y-4 text-[15px]">
                <div>
                  <h4 className="font-semibold mb-1">Precise Location</h4>
                  <p className="text-muted-foreground">
                    With your permission, we access your device's precise location to show nearby cafes and enable proximity-based check-ins. We do not store your precise location, with one exception: if you ask to be told when Grounded reaches your area, we store an approximate location for that request as described below.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">City Waitlist</h4>
                  <p className="text-muted-foreground">
                    When you ask us to bring Grounded to your area, we store the city and state we resolved for you, an approximate location rounded to about one kilometre, and your email address if you gave one. We use this to decide which cities to score next and to email you once when your area goes live.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">User Content</h4>
                  <p className="text-muted-foreground">
                    Photos you upload, reviews you write, check-in notes, feedback submissions, and WiFi speed or noise-level measurements you contribute. This content is associated with your account and may be visible to other users.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Search History</h4>
                  <p className="text-muted-foreground">
                    Cafe search queries are sent to Google Places to return results. We do not permanently store your search queries.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">User IDs &amp; Profile Information</h4>
                  <p className="text-muted-foreground">
                    Email address, display name, username, avatar, bio, and favorite drink. This information is used to identify your account and personalize your experience.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Product Interaction</h4>
                  <p className="text-muted-foreground">
                    Favorites, check-ins, badge progress, engagement metrics (session counts, action counts), and onboarding preferences such as work motivations and noise preferences.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Crash &amp; Diagnostics Data</h4>
                  <p className="text-muted-foreground">
                    Error logs captured by our in-app error boundary to help us identify and fix bugs. These logs do not contain personal information.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Advertising Identifier</h4>
                  <p className="text-muted-foreground">
                    With your permission (via Apple's App Tracking Transparency prompt), we collect your device's advertising identifier (IDFA) to measure which ads led you to download Grounded. You can decline this at any time, and the app works fully without it.
                  </p>
                </div>
              </AccordionContent>

            </AccordionItem>

            {/* 2 — How We Use Your Data */}
            <AccordionItem value="data-use">
              <AccordionTrigger className="text-base font-semibold">
                2. How We Use Your Data
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li>Show you cafes near your current location</li>
                  <li>Display community reviews, photos, and check-in data</li>
                  <li>Personalize recommendations based on your preferences</li>
                  <li>Calculate work-readiness scores from aggregated check-in data</li>
                  <li>Award badges based on your activity</li>
                  <li>Send notifications about activity relevant to you (e.g., photo likes, new cafes)</li>
                  <li>Email you once when Grounded launches in an area you asked us about</li>
                  <li>Diagnose and fix technical issues</li>
                  <li>Improve the app based on aggregated usage patterns</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 3 — Data Sharing */}
            <AccordionItem value="data-sharing">
              <AccordionTrigger className="text-base font-semibold">
                3. Data Sharing &amp; Third Parties
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>We do not sell your personal data. We share limited data with the following services to operate the app:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong>Google Places API</strong> — search queries are sent to Google to return cafe search results.</li>
                  <li><strong>OpenFreeMap</strong>: map tiles are requested from OpenFreeMap to draw map views. They receive your IP address and which tiles you request, which indicates the approximate area you are viewing.</li>
                  <li><strong>Lovable Cloud</strong> — our backend infrastructure provider for authentication, database, and file storage.</li>
                  <li><strong>AppsFlyer</strong> — We use AppsFlyer to measure the effectiveness of our advertising and understand how users discover Grounded. AppsFlyer may process your device's advertising identifier and install and interaction data for attribution and analytics purposes.</li>

                </ul>
                <p>User-generated content (reviews, photos) is visible to other Grounded users.</p>
              </AccordionContent>
            </AccordionItem>

            {/* 4 — Data Retention */}
            <AccordionItem value="data-retention">
              <AccordionTrigger className="text-base font-semibold">
                4. Data Retention
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <p>
                  We retain your account data and content for as long as your account is active. If you delete your account, we remove your personal data within 30 days. Aggregated, anonymized data (e.g., average WiFi scores) may be retained indefinitely. For city waitlist entries, we keep your email address and approximate location until you unsubscribe or delete your account, so we can let you know when Grounded reaches your area and as it grows there.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 5 — Your Rights */}
            <AccordionItem value="your-rights">
              <AccordionTrigger className="text-base font-semibold">
                5. Your Rights
              </AccordionTrigger>
              <AccordionContent className="space-y-2 text-[15px] text-muted-foreground">
                <ul className="list-disc pl-5 space-y-1.5">
                  <li><strong>Delete your account</strong> — You can delete your account from the Profile page. This removes your personal data, reviews, photos, and check-ins.</li>
                  <li><strong>Opt out of emails</strong> — You can unsubscribe from notification emails in your profile settings.</li>
                  <li><strong>City waitlist</strong> — If you joined without an account, the unsubscribe link in any Grounded email removes your email address from the waitlist as well.</li>
                  <li><strong>Location access</strong> — You can revoke location permissions at any time through your device settings.</li>
                  <li><strong>Data access</strong> — Contact us to request a copy of your data.</li>
                  <li><strong>Ad tracking</strong> — You can opt out of advertising attribution at any time by declining the App Tracking Transparency prompt or disabling tracking in your device settings.</li>

                </ul>
              </AccordionContent>
            </AccordionItem>

            {/* 6 — Contact */}
            <AccordionItem value="contact">
              <AccordionTrigger className="text-base font-semibold">
                6. Contact Us
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-muted-foreground">
                <p>
                  If you have questions about this Privacy Policy, please reach out through the in-app feedback form or email us at{" "}
                  <a href="mailto:hello@getgrounded.app" className="text-primary hover:underline">
                    hello@getgrounded.app
                  </a>.
                </p>
              </AccordionContent>
            </AccordionItem>

            {/* 7 — Changes */}
            <AccordionItem value="changes">
              <AccordionTrigger className="text-base font-semibold">
                7. Changes to This Policy
              </AccordionTrigger>
              <AccordionContent className="text-[15px] text-muted-foreground">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy in the app. Your continued use of Grounded after changes are posted constitutes acceptance of the revised policy.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </main>
      <Footer />
      <BackToTopButton />
    </div>
  );
}
